import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as signalR from '@microsoft/signalr';
import { fetchExpensesByJob } from '../api/expensesApi';

const HUB_URL = `${import.meta.env.VITE_API_URL}/hub/liverefresh`;

// Scoped to a job — the key includes `jobId` so caches for different jobs
// stay isolated. Invalidates on BOTH UpdateExpenseTable (create / update /
// delete) and UpdateReceiptTable (upload / delete) because receipt count is
// a projected field on the expense rows.
export default function useExpensesByJob(jobId) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ['expenses', jobId],
        queryFn: () => fetchExpensesByJob(jobId),
        enabled: jobId != null,
        retry: false,
    });

    useEffect(() => {
        if (jobId == null) return undefined;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, {
                accessTokenFactory: () =>
                    window.Clerk.session.getToken({ template: 'marginpal-api' }),
            })
            .withAutomaticReconnect()
            .build();

        const invalidate = () => {
            queryClient.invalidateQueries({ queryKey: ['expenses', jobId] });
            queryClient.invalidateQueries({ queryKey: ['billingUsage'] });
        };

        connection.on('UpdateExpenseTable', invalidate);
        connection.on('UpdateReceiptTable', invalidate);
        // Accounting sync state is a projected field on each expense row.
        connection.on('UpdateAccountingStatus', invalidate);

        connection.start().catch(err => console.error('SignalR connection error:', err));

        return () => {
            connection.stop();
        };
    }, [queryClient, jobId]);

    return query;
}
